// src/components/cabling/CablingProgressTable.jsx
import React from 'react';
import { COLORS } from '../../constants';
import { DashboardSection, PAGE } from '../ui';

export default function CablingProgressTable({
  rows = [],
  title = 'Cabling Progress',
  subtitle = 'BOM comparison with cabling progress',
  type = 'copper',
}) {
  const getQty = (row) => {
    if (type === 'fibre') return Number(row.qty || row.count || 0) || 0;
    return Number(row.qty || 0) || 0;
  };

  const getCompleted = (row) => {
    if (type === 'fibre') {
      if (row.completed !== undefined && row.completed !== null) {
        return Number(row.completed) || 0;
      }

      const roughed = Number(row.roughedIn || 0);
      const dressed = Number(row.dressedIn || 0);
      const tested = Number(row.tested || 0);
      const terminated = Number(row.terminated || 0);

      return Math.max(roughed, dressed, tested, terminated);
    }

    return Number(row.roughedIn || row.roughed || 0) || 0;
  };

  const totalQty = rows.reduce((acc, row) => acc + getQty(row), 0);
  const totalCompleted = rows.reduce((acc, row) => acc + getCompleted(row), 0);

  const overallProgress =
    totalQty > 0 ? Math.min(100, Math.round((totalCompleted / totalQty) * 100)) : 0;

  const getStatusStyle = (status) => {
    const s = String(status || '').toLowerCase();

    if (s === 'completed' || s === 'updated') {
      return { background: COLORS.successLight, color: COLORS.success };
    }

    if (s === 'in progress') {
      return { background: COLORS.warningLight, color: COLORS.warning };
    }

    if (s === 'not updated' || s === 'not started') {
      return { background: '#fff7ed', color: COLORS.orange };
    }

    return { background: COLORS.dangerLight, color: COLORS.danger };
  };

  const getPrimaryLabel = () => {
    if (type === 'fibre') return 'Bundle / Route';
    return 'Product Code';
  };

  const getPrimaryValue = (row, index) => {
    if (type === 'fibre') {
      const route = [row.rackA, row.rackB].filter(Boolean).join(' → ');
      if (row.bundle && route) return `${row.bundle} · ${route}`;
      return row.bundle || route || row.productCode || `Fibre ${index + 1}`;
    }

    return row.productCode || row.description || `Cable ${index + 1}`;
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return COLORS.success;
    if (progress > 0) return COLORS.warning;
    return COLORS.slate;
  };

  const colWidths =
    type === 'fibre'
      ? {
          primary: 'auto',
          qty: 70,
          roughed: 90,
          dressed: 90,
          tested: 80,
          term: 80,
          rem: 80,
          progress: 150,
          status: 120,
        }
      : {
          primary: 'auto',
          qty: 70,
          roughed: 100,
          dressed: 100,
          rem: 90,
          progress: 160,
          status: 130,
        };

  const thStyle = (width, align = 'center', extra = {}) => ({
    width,
    position: 'sticky',
    top: 0,
    zIndex: 100,
    padding: '10px 12px',
    fontSize: 11,
    fontWeight: 800,
    color: '#475569',
    textTransform: 'uppercase',
    textAlign: align,
    borderBottom: `1px solid ${COLORS.line}`,
    background: '#f1f5f9',
    whiteSpace: 'nowrap',
    boxShadow: '0 1px 0 #e2e8f0, 0 4px 10px rgba(15, 23, 42, 0.08)',
    ...extra,
  });

  const tdStyle = (width, align = 'center', extra = {}) => ({
    width,
    padding: '10px 12px',
    fontSize: 12,
    fontWeight: 500,
    color: COLORS.text,
    textAlign: align,
    borderBottom: `1px solid ${COLORS.line}`,
    ...extra,
  });

  if (rows.length === 0) {
    return (
      <DashboardSection title={title} subtitle={subtitle}>
        <div style={{ textAlign: 'center', padding: 40, color: COLORS.textSecondary }}>
          No {type} cabling data available
        </div>
      </DashboardSection>
    );
  }

  return (
    <DashboardSection
      title={title}
      subtitle={subtitle}
      headerExtra={
        <span
          style={{
            padding: '4px 10px',
            borderRadius: 999,
            background:
              overallProgress >= 100
                ? COLORS.successLight
                : overallProgress > 0
                  ? COLORS.warningLight
                  : PAGE.colors.bg,
            color: getProgressColor(overallProgress),
            fontSize: 11,
            fontWeight: 800,
          }}
        >
          {overallProgress}% complete
        </span>
      }
    >
      {/* Important: no inner overflow:auto here. The parent .cabling-table-wrapper owns scrolling,
          so sticky table headers stay fixed while the table body scrolls. */}
      <div
        style={{
          width: '100%',
          overflow: 'visible',
          background: COLORS.surface,
          borderRadius: 14,
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            tableLayout: 'fixed',
            minWidth: type === 'fibre' ? 820 : 650,
          }}
        >
          <colgroup>
            <col style={{ width: colWidths.primary }} />
            <col style={{ width: colWidths.qty }} />
            <col style={{ width: colWidths.roughed }} />
            <col style={{ width: colWidths.dressed }} />
            {type === 'fibre' && <col style={{ width: colWidths.tested }} />}
            {type === 'fibre' && <col style={{ width: colWidths.term }} />}
            <col style={{ width: colWidths.rem }} />
            <col style={{ width: colWidths.progress }} />
            <col style={{ width: colWidths.status }} />
          </colgroup>

          <thead>
            <tr>
              <th style={thStyle(colWidths.primary, 'left', { borderTopLeftRadius: 14 })}>
                {getPrimaryLabel()}
              </th>
              <th style={thStyle(colWidths.qty)}>Qty</th>
              <th style={thStyle(colWidths.roughed)}>
                {type === 'fibre' ? 'Roughed' : 'Roughed In'}
              </th>
              <th style={thStyle(colWidths.dressed)}>Dressed</th>
              {type === 'fibre' && <th style={thStyle(colWidths.tested)}>Tested</th>}
              {type === 'fibre' && <th style={thStyle(colWidths.term)}>Term.</th>}
              <th style={thStyle(colWidths.rem)}>Rem.</th>
              <th style={thStyle(colWidths.progress, 'left')}>Progress</th>
              <th style={thStyle(colWidths.status, 'center', { borderTopRightRadius: 14 })}>
                Status
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row, index) => {
              const qty = getQty(row);
              const completed = getCompleted(row);

              const progress =
                row.progress !== undefined
                  ? Number(row.progress)
                  : qty > 0
                    ? Math.min(100, Math.round((completed / qty) * 100))
                    : 0;

              const status =
                row.status ||
                (progress >= 100 ? 'Completed' : progress > 0 ? 'In Progress' : 'Not Updated');

              const statusStyle = getStatusStyle(status);

              const remainingValue =
                row.remaining !== undefined
                  ? row.remaining
                  : Math.max(0, qty - completed);

              const rowBg = index % 2 === 0 ? COLORS.surface : '#fafbfc';

              return (
                <tr key={index} style={{ background: rowBg }}>
                  <td
                    style={tdStyle(colWidths.primary, 'left', {
                      fontWeight: 700,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    })}
                  >
                    {getPrimaryValue(row, index)}
                  </td>

                  <td style={tdStyle(colWidths.qty)}>{qty}</td>

                  <td style={tdStyle(colWidths.roughed)}>
                    {row.roughedIn || row.roughed || 0}
                  </td>

                  <td style={tdStyle(colWidths.dressed)}>
                    {row.dressedIn || row.dressed || '—'}
                  </td>

                  {type === 'fibre' && (
                    <td style={tdStyle(colWidths.tested)}>{row.tested || 0}</td>
                  )}

                  {type === 'fibre' && (
                    <td style={tdStyle(colWidths.term)}>{row.terminated || 0}</td>
                  )}

                  <td
                    style={tdStyle(colWidths.rem, 'center', {
                      color: remainingValue > 0 ? COLORS.warning : COLORS.success,
                      fontWeight: 700,
                    })}
                  >
                    {remainingValue}
                  </td>

                  <td style={tdStyle(colWidths.progress, 'left')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div
                        style={{
                          flex: 1,
                          height: 6,
                          background: COLORS.line,
                          borderRadius: 999,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${progress}%`,
                            height: '100%',
                            background: getProgressColor(progress),
                            borderRadius: 999,
                          }}
                        />
                      </div>

                      <span style={{ minWidth: 36, fontSize: 11, fontWeight: 700 }}>
                        {progress}%
                      </span>
                    </div>
                  </td>

                  <td style={tdStyle(colWidths.status)}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 9px',
                        borderRadius: 999,
                        fontSize: 10,
                        fontWeight: 800,
                        whiteSpace: 'nowrap',
                        ...statusStyle,
                      }}
                    >
                      {status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </DashboardSection>
  );
}
